import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, FileText, FolderOpen, Users, Package, UserCog,
  BarChart3, ScrollText, Settings as SettingsIcon, LogOut, UserCircle, Factory,
  Paperclip, FileSignature, Receipt, DownloadCloud, ArrowLeftRight, ChevronDown, ChevronRight, History,
  Briefcase, FileSpreadsheet, Boxes, Truck, Route, Wrench, Clock, Search, ClipboardList,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import OASearchSelect from "./OASearchSelect";

const WARN_MS = 5 * 60 * 1000;     // show warning 5 min before session end
const REFRESH_GAP_MS = 60 * 1000;  // slide session at most once a minute on activity

export default function AppLayout() {
  const { user, logout, deadline, refresh } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [oaOpen, setOaOpen] = useState(true);
  const [warn, setWarn] = useState(false);
  const [remain, setRemain] = useState(0);
  const activeRef = useRef(false);
  const lastRefreshRef = useRef(Date.now());

  // Track real user activity (mouse, keyboard, click, scroll, navigation).
  useEffect(() => {
    const mark = () => { activeRef.current = true; };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => evts.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  // Enforce the server-side session policy on the client: warn, slide on activity, logout when expired.
  useEffect(() => {
    if (!deadline) return;
    const tick = async () => {
      const left = deadline - Date.now();
      setRemain(Math.max(0, left));
      if (left <= 0) { logout(true); return; }
      if (left <= WARN_MS) { setWarn(true); return; }
      setWarn(false);
      // Active user, healthy session -> slide the token so they aren't logged out mid-work.
      if (activeRef.current && Date.now() - lastRefreshRef.current > REFRESH_GAP_MS) {
        activeRef.current = false;
        lastRefreshRef.current = Date.now();
        refresh();
      }
    };
    tick();
    const iv = setInterval(tick, 10000);
    return () => clearInterval(iv);
  }, [deadline]); // eslint-disable-line

  const continueSession = async () => { const ok = await refresh(); if (ok) { setWarn(false); lastRefreshRef.current = Date.now(); } };
  const fmt = (ms) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

  const isAdmin = user?.role === "admin";
  const can = (p) => isAdmin || (user?.permissions || []).includes(p);

  const oaSub = [
    { to: "/oa/new", label: "Create OA", show: can("create_oa") },
    { to: "/oa", label: "OA History", show: can("oa_history") || can("view_oa"), end: true },
    { to: "/oa?window=all", label: "View All OA", show: can("view_oa_all") },
    { to: "/oa?window=15", label: "15 Days OA", show: can("view_oa_15d") },
    { to: "/oa?window=30", label: "30 Days OA", show: can("view_oa_30d") },
    { to: "/oa?window=90", label: "3 Months OA", show: can("view_oa_3m") },
    { to: "/oa?window=180", label: "6 Months OA", show: can("view_oa_6m") },
    { to: "/oa?window=365", label: "1 Year OA", show: can("view_oa_1y") },
  ].filter((n) => n.show);

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, show: true },
    { to: "/po", label: "View All PO", icon: FolderOpen, show: can("view_all_po") },
    { to: "/records/office-work", label: "Office Work", icon: Briefcase, show: can("view_office_work") },
    { to: "/customers", label: "Customers", icon: Users, show: true },
    { to: "/work-orders", label: "Work Order", icon: ClipboardList, show: can("view_workorder") },
    { to: "/records/item-tracking", label: "Item Tracking", icon: Boxes, show: can("view_item") },
    { to: "/records/invoice-data", label: "Invoice", icon: FileSpreadsheet, show: can("view_invoice") },
    { to: "/records/dispatch", label: "Dispatch", icon: Truck, show: can("view_dispatch") },
    { to: "/records/transporter", label: "Transporter", icon: Route, show: can("view_transporter") },
    { to: "/records/installer", label: "Installer", icon: Wrench, show: can("view_installer") },
    { to: "/annexure", label: "Annexure", icon: Paperclip, show: can("view_annexure") },
    { to: "/quotation", label: "Quotation", icon: FileSignature, show: can("view_quotation") },
    { to: "/bill", label: "Bill", icon: Receipt, show: can("view_bill") },
    { to: "/oa-search", label: "OA Search", icon: Search, show: can("view_oa") },
    { to: "/masters", label: "Master Data", icon: Boxes, show: can("view_master") },
    { to: "/import-old-oa", label: "Import Old OA", icon: DownloadCloud, show: isAdmin || can("import_data") },
    { to: "/reports", label: "Reports", icon: BarChart3, show: can("reports") },
    { to: "/products", label: "Products", icon: Package, show: true },
    ...(isAdmin ? [{ to: "/employees", label: "Employees", icon: UserCog, show: true }, { to: "/audit-logs", label: "Audit Logs", icon: ScrollText, show: true }] : []),
    { to: "/import-export", label: "Import / Export", icon: ArrowLeftRight, show: isAdmin || can("import_data") || can("export_data") },
    ...(isAdmin ? [{ to: "/settings", label: "Settings", icon: SettingsIcon, show: true }] : []),
    { to: "/profile", label: "Profile", icon: UserCircle, show: true },
  ].filter((n) => n.show);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "nav-active" : "text-slate-300 hover:bg-white/10"}`;

  return (
    <div className="min-h-screen flex bg-slate-100">
      <aside className="no-print app-sidebar w-64 shrink-0 text-slate-100 flex flex-col fixed h-screen">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          {branding.logo ? (
            <img src={branding.logo} alt="logo" className="h-10 w-10 rounded-lg object-contain bg-white/10 p-1" />
          ) : (
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: "var(--brand)" }}>
              <Factory className="h-6 w-6 text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-head font-bold text-sm leading-tight truncate">{branding.shortName || branding.companyName}</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">OA Manager</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          <NavLink to="/dashboard" data-testid="nav-dashboard" className={linkCls}><LayoutDashboard className="h-[18px] w-[18px]" /> Dashboard</NavLink>
          {oaSub.length > 0 && (
            <div>
              <button onClick={() => setOaOpen((v) => !v)} data-testid="nav-oa-group" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/10">
                <History className="h-[18px] w-[18px]" /> OA
                {oaOpen ? <ChevronDown className="h-4 w-4 ml-auto" /> : <ChevronRight className="h-4 w-4 ml-auto" />}
              </button>
              {oaOpen && (
                <div className="ml-4 border-l border-white/10 pl-2 mt-1 space-y-1">
                  {oaSub.map((s) => (
                    <NavLink key={s.to} to={s.to} end={s.end}
                      data-testid={`nav-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => `block px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "nav-active" : "text-slate-400 hover:bg-white/10"}`}>
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          {nav.filter((n) => n.to !== "/dashboard").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/oa"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`} className={linkCls}>
              <n.icon className="h-[18px] w-[18px]" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--brand)" }}>{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="btn-ghost w-full text-slate-300 hover:text-white hover:bg-white/10 mt-1">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-h-screen">
        <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-6 py-2.5">
          <div className="w-full max-w-md">
            <OASearchSelect onPick={(o) => navigate(`/oa/${o.id}/details`)} testid="global-oa-search" placeholder="Global O.A. Search — type O.A. Number…" />
          </div>
        </div>
        <Outlet />
      </main>
      {warn && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="session-warning-modal">
          <div className="card w-full max-w-sm p-6 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mb-3"><Clock className="h-6 w-6 text-amber-600" /></div>
            <h3 className="font-head text-lg font-bold text-slate-900">Your session will expire soon</h3>
            <p className="text-sm text-slate-500 mt-1">You will be logged out in <span className="font-mono font-bold text-slate-800" data-testid="session-countdown">{fmt(remain)}</span>. Do you want to stay signed in?</p>
            <div className="flex gap-2 mt-5">
              <button className="btn-outline flex-1" onClick={() => logout(false)} data-testid="session-logout-btn">Logout</button>
              <button className="btn-primary flex-1" onClick={continueSession} data-testid="session-continue-btn">Continue Session</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
